/** Path to a patient's chart, scoped to a specific episode. */
export function patientEpisodePath(
  patientId: string,
  episodeId: string
): string {
  return `/patients/${patientId}?episode=${episodeId}`;
}

/** Path to a specific visit's chart, optionally scoped to an episode. */
export function patientVisitPath(
  patientId: string,
  visitId: string,
  episodeId?: string
): string {
  const base = `/patients/${patientId}/visits/${visitId}`;
  return episodeId ? `${base}?episode=${episodeId}` : base;
}

/** Deep link to the Authorizations tab for a patient, optionally scoped to an episode. */
export function patientAuthorizationsPath(
  patientId: string,
  episodeId?: string
): string {
  const base = `/patients/${patientId}/authorizations`;
  return episodeId ? `${base}?episode=${episodeId}` : base;
}

type OrderManagerTabId = 'to-be-sent' | 'pending-signature' | 'received';

/**
 * Maps an order's TaskStatus to the Orders Manager tab that will contain it,
 * or null when the status has no tab. NOTE: the `received` tab covers a bucket
 * of two statuses on the backend (returned_with_signature +
 * sent_no_signature_required), so this is not a plain inverse of STATUS_MAP.
 */
export function orderManagerTabForStatus(
  status: string
): OrderManagerTabId | null {
  switch (status) {
    case 'completed':
      return 'to-be-sent';
    case 'sent_to_physician':
      return 'pending-signature';
    case 'returned_with_signature':
    case 'sent_no_signature_required':
      return 'received';
    default:
      return null;
  }
}

/** Orders Manager deep link that highlights an order; adds &tab when known. */
export function ordersHighlightPath(
  orderId: string,
  tabId?: string | null
): string {
  const base = `/orders?highlightOrder=${orderId}`;
  return tabId ? `${base}&tab=${tabId}` : base;
}

/**
 * Billing order deep link. Orders represented in Orders Manager tabs keep the
 * existing highlighted manager destination; earlier task statuses fall back to
 * the patient-scoped task route, which opens OrderDetailPanel for order tasks.
 */
export function billingOrderPath({
  orderId,
  status,
  patientId,
  episodeId,
}: {
  orderId: string;
  status: string;
  patientId: string;
  episodeId?: string | null;
}): string {
  const orderTab = orderManagerTabForStatus(status);
  if (orderTab) {
    return ordersHighlightPath(orderId, orderTab);
  }
  return patientVisitPath(patientId, orderId, episodeId ?? undefined);
}

/** Deep link to the patient info tab's insurance section with scrolling/highlighting. */
export function patientInsuranceSectionPath(patientId: string): string {
  return `/patients/${patientId}/info?section=insurance`;
}
